/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { connect } from 'react-redux';
import React, { Component } from 'react';
import AcceptFlagActionView from './AcceptFlagAction.view';
import { acceptFlagAndDeleteContent, changeFlagStatus } from '../../store/FlaggedContent.actions';
import { UserActionTypes } from '../../../users/store/UserActionTypes';
import { ActionMenuItemProps } from '../../../../shared/views/ActionMenu/ActionMenu';
import { PlatformAdminUIState, PlatformAdminUIThunkType } from '../../../../store/store';
import { ClientUserGeneratedContentFlagStatusChangeRequestNewStatusEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import { FlaggedContentState, FlaggedType } from '../../store/FlaggedContent.state';

interface Props extends ActionMenuItemProps {
	flag: FlaggedContentState['detail'];
	acceptFlagAndDeleteContent: (flagId: string, comment: string) => void;
	changeFlagStatus: (
		flagId: string,
		newStatus: ClientUserGeneratedContentFlagStatusChangeRequestNewStatusEnum,
		comment: string,
		snackbarMessage?: string,
		returnToTable?: boolean
	) => void;
}

class AcceptFlagActionContainer extends Component<Props> {
	constructor(props: Props) {
		super(props);
		this.handleStatusChangeAction = this.handleStatusChangeAction.bind(this);
	}

	handleStatusChangeAction(comment: string, deleteAndNotify: boolean): void {
		const { flag, acceptFlagAndDeleteContent, changeFlagStatus } = this.props;
		if (deleteAndNotify) {
			acceptFlagAndDeleteContent(flag.flagId, comment);
			return;
		}
		changeFlagStatus(
			flag.flagId,
			ClientUserGeneratedContentFlagStatusChangeRequestNewStatusEnum.Accepted,
			comment,
			'Fall abgeschlossen. Der Status wurde auf "Akzeptiert" gesetzt.',
			true
		);
	}

	render(): JSX.Element {
		const { flag, renderMenuItem } = this.props;
		const alreadyDeleted = !!(
			(flag.type === FlaggedType.POST && flag.post?.postDeleted) ||
			(flag.type === FlaggedType.COMMENT && flag.comment?.flagComment?.deleted)
		);
		const supportsDeletion = flag.type === FlaggedType.POST || flag.type === FlaggedType.COMMENT;

		return (
			<AcceptFlagActionView
				supportsDeletion={supportsDeletion}
				renderMenuItem={renderMenuItem}
				handleStatusChangeAction={this.handleStatusChangeAction}
				entityAlreadyDeleted={alreadyDeleted}
			/>
		);
	}
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: PlatformAdminUIState) => {
	return {
		flag: state.flaggedContent.detail
	};
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapDispatchToProps = (dispatch: PlatformAdminUIThunkType<UserActionTypes>) => {
	return {
		acceptFlagAndDeleteContent: (flagId: string, comment: string): Promise<void> =>
			dispatch(acceptFlagAndDeleteContent(flagId, comment)),
		changeFlagStatus: (
			flagId: string,
			newStatus: ClientUserGeneratedContentFlagStatusChangeRequestNewStatusEnum,
			comment: string,
			snackbarMessage?: string,
			returnToTable?: boolean
		): Promise<void> => dispatch(changeFlagStatus(flagId, newStatus, comment, snackbarMessage, returnToTable))
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(AcceptFlagActionContainer);
